import { useEffect } from "react";

import { DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, ORIGIN, SITE_NAME, TWITTER_HANDLE } from "../data/seo";

/**
 * Sets the document title, meta description, canonical link, and the Open
 * Graph / Twitter card tags for the current page.
 *
 * WHY NOT react-helmet: helmet works by mutating the document head in an
 * effect, which is exactly what this does. Adding it would mean changing
 * front/package.json, which makes scripts/deploy.sh take its npm install
 * branch and re-resolve ~1,300 packages against a 2021 dependency tree. Not
 * worth the risk for behaviour we get in a hundred lines.
 *
 * This is client-side, so the tags appear only once React has rendered.
 * Googlebot renders JavaScript and will see them; crawlers that do not (Bing,
 * and social preview bots) still see the static fallback in index.html.
 *
 * That last point matters specifically for Open Graph: Facebook, WhatsApp and
 * LinkedIn scrapers do NOT execute JavaScript, so they read whatever is in
 * the served HTML. These tags are still worth setting - they are correct for
 * anything that does render, and they are the half of the work that does not
 * need prerendering - but link previews will not be per-listing until the
 * site is prerendered or server-rendered.
 *
 * Usage:
 *   useSeo({ title, description, canonical, image, imageAlt, type, video });
 *
 * `video` is optional: { url, width, height } for the embed - see postSeo()
 * in data/seo.js. Pass null/undefined while data is loading and the previous
 * values are left alone, so nothing flickers through an empty string.
 */
export default function useSeo(seo) {
    const title = seo && seo.title;
    const description = seo && seo.description;
    const canonical = seo && seo.canonical;
    const image = seo && seo.image;
    const imageAlt = seo && seo.imageAlt;
    const type = (seo && seo.type) || "website";
    const publishedTime = seo && seo.publishedTime;
    const modifiedTime = seo && seo.modifiedTime;
    const noindex = !!(seo && seo.noindex);
    const video = seo && seo.video;

    useEffect(() => {
        if (title) {
            document.title = title;
            setMeta("property", "og:title", title);
            setMeta("name", "twitter:title", title);
        }

        if (description) {
            setMeta("name", "description", description);
            setMeta("property", "og:description", description);
            setMeta("name", "twitter:description", description);
        }

        // Always publish a canonical. Both www and the apex answer on 200 at
        // the nginx level only because of a 301 added later; the tag makes the
        // preferred URL explicit regardless.
        const href = canonical || ORIGIN + window.location.pathname;
        setCanonical(href);
        setMeta("property", "og:url", href);

        setMeta("property", "og:type", type);
        setMeta("property", "og:site_name", SITE_NAME);
        setMeta("property", "og:locale", "en_GB");

        const img = image || DEFAULT_OG_IMAGE;
        setMeta("property", "og:image", img);
        setMeta("name", "twitter:image", img);
        if (imageAlt) {
            setMeta("property", "og:image:alt", imageAlt);
            setMeta("name", "twitter:image:alt", imageAlt);
        }

        // summary_large_image gives a property photo the space it deserves;
        // a plain summary card crops it to a thumbnail. "player" is what
        // makes X render the embed inline instead - see the video block
        // below, which is the only thing allowed to switch this.
        setMeta("name", "twitter:card", video ? "player" : "summary_large_image");
        setMeta("name", "twitter:site", TWITTER_HANDLE);

        if (type === "article") {
            if (publishedTime) setMeta("property", "article:published_time", publishedTime);
            if (modifiedTime) setMeta("property", "article:modified_time", modifiedTime);
        } else {
            removeMeta("property", "article:published_time");
            removeMeta("property", "article:modified_time");
        }

        // Same reasoning as the noindex cleanup below: this is a single-page
        // app, so a tag set on a video post and never removed would follow a
        // visitor onto the next, video-less page they click through to.
        if (video && video.url) {
            setMeta("property", "og:video", video.url);
            setMeta("property", "og:video:secure_url", video.url);
            setMeta("property", "og:video:type", "text/html");
            setMeta("property", "og:video:width", video.width);
            setMeta("property", "og:video:height", video.height);
            setMeta("name", "twitter:player", video.url);
            setMeta("name", "twitter:player:width", video.width);
            setMeta("name", "twitter:player:height", video.height);
        } else {
            removeMeta("property", "og:video");
            removeMeta("property", "og:video:secure_url");
            removeMeta("property", "og:video:type");
            removeMeta("property", "og:video:width");
            removeMeta("property", "og:video:height");
            removeMeta("name", "twitter:player");
            removeMeta("name", "twitter:player:width");
            removeMeta("name", "twitter:player:height");
        }

        // Removing this when the page is indexable is not optional. This is a
        // single-page app: a robots meta left behind after someone visits the
        // 404 page would follow them onto every real page for the rest of the
        // session, and a renderer that sampled one of those would be told not
        // to index it.
        if (noindex) {
            setMeta("name", "robots", "noindex, follow");
        } else {
            removeMeta("name", "robots");
        }
    }, [title, description, canonical, image, imageAlt, type, publishedTime, modifiedTime, noindex, video]);
}

/** attr is "name" for standard/Twitter tags, "property" for Open Graph. */
function setMeta(attr, key, content) {
    let el = document.querySelector("meta[" + attr + '="' + key + '"]');
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function removeMeta(attr, key) {
    const el = document.querySelector("meta[" + attr + '="' + key + '"]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
}

function setCanonical(href) {
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

/** Convenience for pages whose metadata never changes. */
export function useStaticSeo(entry) {
    useSeo(
        entry
            ? {
                  title: entry.title,
                  description: entry.description || DEFAULT_DESCRIPTION,
                  /*
                   * The entry's own canonical where it has one. This used to
                   * be unconditionally ORIGIN + pathname, which was right
                   * while every static page belonged to one site and silently
                   * wrong the moment one did not: the report tool on
                   * doorstepdata.co.uk was pointing its canonical at
                   * property-people.co.uk, telling a search engine the
                   * product's page was really the agency's.
                   */
                  canonical: entry.canonical || ORIGIN + window.location.pathname,
                  image: entry.image,
                  // Lets a STATIC_SEO entry hold a page back from the index
                  // without the page itself knowing. api/render.js already
                  // reads the same flag, so both halves agree.
                  noindex: entry.noindex,
              }
            : { title: SITE_NAME, description: DEFAULT_DESCRIPTION }
    );
}
